import { OverlayModule } from '@angular/cdk/overlay'
import { CdkScrollable } from '@angular/cdk/scrolling'
import {
    BasePortalOutlet,
    CdkPortalOutlet,
    type ComponentPortal,
    PortalModule,
    type TemplatePortal,
} from '@angular/cdk/portal'
import {
    ChangeDetectionStrategy,
    Component,
    type ComponentRef,
    computed,
    ElementRef,
    type EmbeddedViewRef,
    type EventEmitter,
    inject,
    NgModule,
    output,
    type TemplateRef,
    type Type,
    viewChild,
    type ViewContainerRef,
} from '@angular/core'

import { mergeClasses, noopFn } from '@/styles/utils/merge-classes'

import type { ZardDialogRef } from './dialog-ref'
import { ZardDialogService } from './dialog.service'
import { dialogVariants } from './dialog.variants'
import { ZardButtonComponent } from '@/styles/components/button/button.component'
import { ZardIconComponent } from '@/styles/components/icon/icon.component'
import type { ZardIcon } from '@/styles/components/icon/icons'

// Used by the NgModule provider definition

export type OnClickCallback<T> = (instance: T) => false | void | object
export class ZardDialogOptions<T, U> {
    zCancelIcon?: ZardIcon
    zCancelText?: string | null
    zClosable?: boolean
    zContent?: string | TemplateRef<T> | Type<T>
    zCustomClasses?: string
    zData?: U
    zDescription?: string
    zHideFooter?: boolean
    zMaskClosable?: boolean
    zHeight?: string
    zMaxHeight?: string
    zOkDestructive?: boolean
    zOkDisabled?: boolean
    zOkIcon?: ZardIcon
    zOkText?: string | null
    zOnCancel?: EventEmitter<T> | OnClickCallback<T> = noopFn
    zOnOk?: EventEmitter<T> | OnClickCallback<T> = noopFn
    zTitle?: string | TemplateRef<T>
    zViewContainerRef?: ViewContainerRef
    zWidth?: string
    zBackdropClass?: string | string[]
    zPosition?: { top?: string; bottom?: string; left?: string; right?: string }
}

@Component({
    selector: 'z-dialog',
    imports: [OverlayModule, PortalModule, CdkScrollable, ZardButtonComponent, ZardIconComponent],
    template: `
        @if (config.zClosable || config.zClosable === undefined) {
            <button
                type="button"
                data-testid="z-close-header-button"
                z-button
                zType="ghost"
                zSize="sm"
                class="absolute top-1 right-1"
                (click)="onCloseClick()"
            >
                <z-icon zType="x" />
            </button>
        }

        @if (config.zTitle || config.zDescription) {
            <header class="flex flex-col space-y-1.5 text-center sm:text-left p-6 pb-0">
                @if (config.zTitle) {
                    <h4
                        data-testid="z-title"
                        class="text-lg leading-none font-semibold tracking-tight"
                    >
                        {{ config.zTitle }}
                    </h4>

                    @if (config.zDescription) {
                        <p data-testid="z-description" class="text-muted-foreground text-sm">
                            {{ config.zDescription }}
                        </p>
                    }
                }
            </header>
        }

        <main
            cdkScrollable
            class="flex flex-col flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-6"
        >
            <ng-template cdkPortalOutlet />

            @if (isStringContent) {
                <div data-testid="z-content" class="min-h-0" [innerHTML]="config.zContent"></div>
            }
        </main>

        @if (!config.zHideFooter) {
            <footer
                class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-0 sm:space-x-2 p-6 pt-0"
            >
                @if (config.zCancelText !== null) {
                    <button
                        type="button"
                        data-testid="z-cancel-button"
                        z-button
                        zType="outline"
                        (click)="onCloseClick()"
                    >
                        @if (config.zCancelIcon) {
                            <z-icon [zType]="config.zCancelIcon" />
                        }

                        {{ config.zCancelText ?? 'Cancel' }}
                    </button>
                }

                @if (config.zOkText !== null) {
                    <button
                        type="button"
                        data-testid="z-ok-button"
                        z-button
                        [zType]="config.zOkDestructive ? 'destructive' : 'default'"
                        [disabled]="config.zOkDisabled"
                        (click)="onOkClick()"
                    >
                        @if (config.zOkIcon) {
                            <z-icon [zType]="config.zOkIcon" />
                        }

                        {{ config.zOkText ?? 'OK' }}
                    </button>
                }
            </footer>
        }
    `,
    styles: `
        :host {
            opacity: 1;
            transform: scale(1);
            transition:
                opacity 150ms ease-out,
                transform 150ms ease-out;
        }

        @starting-style {
            :host {
                opacity: 0;
                transform: scale(0.9);
            }
        }

        :host.dialog-leave {
            opacity: 0;
            transform: scale(0.9);
            transition:
                opacity 150ms ease-in,
                transform 150ms ease-in;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[class]': 'classes()',
        '[style.width]': 'config.zWidth ? config.zWidth : null',
        '[style.height]': 'config.zHeight ? config.zHeight : null',
        '[style.max-height]': 'config.zMaxHeight ? config.zMaxHeight : null',
        'animate.enter': 'dialog-enter',
        'animate.leave': 'dialog-leave',
    },
    exportAs: 'zDialog',
})
export class ZardDialogComponent<T, U> extends BasePortalOutlet {
    private readonly host = inject(ElementRef<HTMLElement>)
    protected readonly config = inject(ZardDialogOptions<T, U>)

    protected readonly classes = computed(() =>
        mergeClasses(dialogVariants(), this.config.zCustomClasses),
    )
    dialogRef?: ZardDialogRef<T>

    protected readonly isStringContent = typeof this.config.zContent === 'string'

    readonly portalOutlet = viewChild.required(CdkPortalOutlet)

    okTriggered = output<void>()
    cancelTriggered = output<void>()

    constructor() {
        super()
    }

    getNativeElement(): HTMLElement {
        return this.host.nativeElement
    }

    attachComponentPortal<T>(portal: ComponentPortal<T>): ComponentRef<T> {
        if (this.portalOutlet()?.hasAttached()) {
            throw new Error('Attempting to attach modal content after content is already attached')
        }
        return this.portalOutlet()?.attachComponentPortal(portal)
    }

    attachTemplatePortal<C>(portal: TemplatePortal<C>): EmbeddedViewRef<C> {
        if (this.portalOutlet()?.hasAttached()) {
            throw new Error('Attempting to attach modal content after content is already attached')
        }

        return this.portalOutlet()?.attachTemplatePortal(portal)
    }

    onOkClick() {
        this.okTriggered.emit()
    }

    onCloseClick() {
        this.cancelTriggered.emit()
    }
}

@NgModule({
    imports: [ZardButtonComponent, ZardDialogComponent, OverlayModule, PortalModule],
    providers: [ZardDialogService],
})
export class ZardDialogModule {}
